import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Stack, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { RevenueByUserReport } from '../../../types/reporting';
import { reportingService } from '../../../services/reportingService';

interface RevenueByUserChartProps {
  data?: RevenueByUserReport;
  loading?: boolean;
}

const RevenueByUserChart: React.FC<RevenueByUserChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const users = data?.users ?? [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Revenue by User</Typography>
          {data && (
            <Chip
              size="small"
              label={`Total: ${reportingService.formatCurrency(data.totalRevenue)}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart
              data={users}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                type="number"
                tickFormatter={(value) => reportingService.formatCurrency(Number(value))}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                type="category"
                dataKey="userName"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
                width={120}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 4,
                }}
                labelStyle={{ color: theme.palette.text.primary }}
                formatter={(value: number, name: string) =>
                  name === 'Revenue' ? reportingService.formatCurrency(value) : value
                }
              />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill={theme.palette.primary.main} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RevenueByUserChart;
